import { useParams } from 'react-router-dom'
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb'
import { BlogPostList } from '@/components/website/BlogPostList'
import { BlogSidebar } from '@/components/website/BlogSidebar'
import { useWebsiteBlogMeta, useWebsiteBlogs } from '@/services/httpServices/queries/useWebsite'
import { flattenBlogTree } from '@/data/blog'

interface BlogArchiveProps {
  /** Whether the archive is filtered by category or tag. */
  kind: 'category' | 'tag'
}

/**
 * Dynamic blog archive page (`/blog/category/:category` and `/blog/tag/:tag`).
 * The archive slug comes straight from the URL and is used to filter the posts
 * returned by the public API (or the bundled demo content when offline).
 */
export function BlogArchive({ kind }: BlogArchiveProps) {
  const params = useParams()
  const slug = (kind === 'category' ? params.category : params.tag) ?? ''
  const { posts, isLoading } = useWebsiteBlogs(
    kind === 'category' ? { categories: [slug] } : { tags: [slug] },
  )
  const { categories, tagTree } = useWebsiteBlogMeta()

  const tree = flattenBlogTree(kind === 'category' ? categories : tagTree)
  const node = tree.find(
    (item) => item.slug === slug || item.name.toLowerCase() === slug.toLowerCase(),
  )
  const basePath = `/blog/${kind === 'category' ? 'category' : 'tag'}`

  const crumbItems = [
    { label: 'Home', to: '/' },
    { label: 'Our Blog', to: '/blog' },
    ...(node?.path ?? [{ id: '', name: slug, slug }]).map((item) => ({
      label: item.name,
      to: `${basePath}/${encodeURIComponent(item.slug)}`,
    })),
  ]

  const emptyMessage =
    kind === 'category'
      ? `No posts found in the “${node?.name ?? slug}” category.`
      : `No posts found tagged “${node?.name ?? slug}”.`

  return (
    <>
      <PageBreadcrumb title={node?.name ?? slug} items={crumbItems} />

      <section className="blog-section pt-100 md-pt-80 pb-100 md-pb-80">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="mb-4">
                <h2 className="text-2xl font-semibold">
                  {kind === 'category' ? 'Category' : 'Tag'}: {node?.name ?? slug}
                </h2>
                <p className="text-muted mb-0">{posts.length} post(s) found</p>
              </div>

              {isLoading && !posts.length ? (
                <div className="flex min-h-[30vh] items-center justify-center">
                  <i className="fa-solid fa-circle-notch fa-spin text-3xl text-primary" aria-label="Loading" />
                </div>
              ) : (
                <BlogPostList posts={posts} emptyMessage={emptyMessage} />
              )}
            </div>

            <div className="col-lg-4">
              <BlogSidebar />
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
